import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';

/**
 * `PivotTable` — a hierarchical table the SPA uses to render the Vendor
 * Profitability pivot (Category > Sub-Category > Vendor > Brand > Product).
 *
 * Why not extend `DataView`?
 *   `DataView` is opinionated about server-paginated, single-level lists
 *   and uses TanStack table without expanded/sub-row models. The pivot
 *   needs (a) a tree shaped from `parent_id`, (b) per-row indentation +
 *   chevrons that respect the level, (c) a per-row "decision" slot, and
 *   (d) CSV export of the *whole* tree, not just the visible page. The
 *   abstraction is different enough to deserve its own component.
 *
 * Performance: the tree is built once with `useMemo`. Visible rows are
 * computed from the expanded set on every render, which is O(n) but n is
 * small (sub-cats + their direct children, plus whatever the user has
 * drilled into) so we don't need virtualization at v1 scale.
 */

export interface PivotNode {
  id: string;
  parent_id: string | null;
  level: string;
  label: string;
  /** Optional warnings the helper attached (e.g. "negative_margin"). */
  warnings?: string[];
  /** Anything else — the column renderer reads from the original row, not from a normalized shape. */
  [key: string]: unknown;
}

export interface PivotColumn<T extends PivotNode> {
  id: string;
  header: ReactNode;
  /** Tooltip describing the math behind this column. */
  helpText?: ReactNode;
  /** Render the cell. Receives the raw row. */
  render: (row: T) => ReactNode;
  /** Optional CSV exporter — receives the raw row, returns a string. Falls back to stripping ReactNode if omitted. */
  toCsv?: (row: T) => string;
  align?: 'left' | 'right';
  /** Pixel width hint for the cell (sticky label column ignores this). */
  width?: number | string;
  className?: string;
}

export interface PivotTableProps<T extends PivotNode> {
  rows: T[];
  columns: PivotColumn<T>[];
  /** Rows to start expanded. Defaults to `[ 'category' ]` so sub-cats are visible. */
  defaultExpandLevels?: string[];
  /** Row click handler — called when the label cell is clicked. */
  onRowSelect?: (row: T) => void;
  /** Currently focused row id, drawn with the active style. */
  selectedId?: string | null;
  /** Slot rendered to the right of the label on rows where it returns truthy. Used for the vendor-decision picker. */
  rowAccessory?: (row: T) => ReactNode;
  /** Headline label shown in the card header. */
  title?: string;
  description?: ReactNode;
  toolbar?: ReactNode;
  /** Hides built-in CSV button. Useful when the page provides its own export. */
  hideExport?: boolean;
  /** Custom CSV name; defaults to "pivot.csv". */
  csvFileName?: string;
  /** Density. */
  density?: 'comfortable' | 'compact';
  /** Empty-state copy. */
  emptyTitle?: string;
  className?: string;
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * Group children under their parents in O(n).
 * `roots` returns nodes with `parent_id === null`. `childrenOf` returns
 * direct children for any id; both ordered by the row's own input order
 * (the helper guarantees a stable depth-first ordering already).
 */
function indexTree<T extends PivotNode>(rows: T[]) {
  const childrenOf = new Map<string, T[]>();
  const byId = new Map<string, T>();
  const roots: T[] = [];

  for (const row of rows) {
    byId.set(row.id, row);
    if (row.parent_id === null || row.parent_id === undefined) {
      roots.push(row);
    } else {
      const existing = childrenOf.get(row.parent_id);
      if (existing) existing.push(row);
      else childrenOf.set(row.parent_id, [row]);
    }
  }

  return { roots, childrenOf, byId };
}

interface VisibleRow<T extends PivotNode> {
  row: T;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
}

function flattenVisible<T extends PivotNode>(
  roots: T[],
  childrenOf: Map<string, T[]>,
  expanded: Set<string>,
): VisibleRow<T>[] {
  const out: VisibleRow<T>[] = [];
  const walk = (row: T, depth: number) => {
    const kids = childrenOf.get(row.id) ?? [];
    const isExpanded = expanded.has(row.id);
    out.push({
      row,
      depth,
      hasChildren: kids.length > 0,
      expanded: isExpanded,
    });
    if (isExpanded) {
      for (const k of kids) walk(k, depth + 1);
    }
  };
  for (const r of roots) walk(r, 0);
  return out;
}

function defaultCsvForCell<T extends PivotNode>(col: PivotColumn<T>, row: T): string {
  if (col.toCsv) return csvEscape(col.toCsv(row));
  const node = col.render(row);
  // Best-effort stringify; complex ReactNodes export as "[node]".
  if (typeof node === 'string' || typeof node === 'number') return csvEscape(String(node));
  return '';
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function PivotTable<T extends PivotNode>({
  rows,
  columns,
  defaultExpandLevels = ['category'],
  onRowSelect,
  selectedId,
  rowAccessory,
  title,
  description,
  toolbar,
  hideExport,
  csvFileName = 'pivot.csv',
  density = 'comfortable',
  emptyTitle = 'No rows match this filter',
  className,
  isLoading,
  isError,
}: PivotTableProps<T>) {
  const tree = useMemo(() => indexTree(rows), [rows]);

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Auto-expand every node whose level is in `defaultExpandLevels`.
    const initial = new Set<string>();
    for (const r of rows) {
      if (defaultExpandLevels.includes(r.level)) initial.add(r.id);
    }
    return initial;
  });

  const visible = useMemo(
    () => flattenVisible(tree.roots, tree.childrenOf, expanded),
    [tree, expanded],
  );

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const next = new Set<string>();
    for (const r of rows) next.add(r.id);
    setExpanded(next);
  };
  const collapseAll = () => {
    const next = new Set<string>();
    for (const r of rows) {
      if (defaultExpandLevels.includes(r.level)) next.add(r.id);
    }
    setExpanded(next);
  };

  const handleExport = () => {
    const header = ['level', 'label', 'path', ...columns.map((c) => String(c.id))].join(',');
    const lines = [header];
    for (const r of rows) {
      const path = Array.isArray((r as unknown as { path?: unknown[] }).path)
        ? ((r as unknown as { path: unknown[] }).path as unknown[]).join(' / ')
        : '';
      const cells = [
        csvEscape(r.level),
        csvEscape(r.label),
        csvEscape(path),
        ...columns.map((c) => defaultCsvForCell(c, r)),
      ];
      lines.push(cells.join(','));
    }
    downloadCsv(csvFileName, lines.join('\n'));
  };

  const cellPaddingY = density === 'compact' ? 'py-1.5' : 'py-2.5';
  const showHeader = Boolean(title || description || toolbar || !hideExport);

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader && (
        <CardHeader className="flex-col gap-2 sm:flex-row sm:items-center">
          <div className="min-w-0">
            {title && <CardTitle className="text-foreground">{title}</CardTitle>}
            {description && (
              <p className="mt-1 text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={collapseAll}
              disabled={!rows.length}
            >
              Collapse all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={expandAll}
              disabled={!rows.length}
            >
              Expand all
            </Button>
            {toolbar}
            {!hideExport && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={!rows.length}
                title="Download as CSV (opens in Excel)"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export CSV
              </Button>
            )}
          </div>
        </CardHeader>
      )}

      <CardBody className="px-0 pb-0 pt-2">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead className="sticky top-0 z-10 bg-surface text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th
                  className={cn(
                    'sticky left-0 z-20 bg-surface px-4 text-left font-medium',
                    cellPaddingY,
                    'border-b border-border',
                  )}
                  style={{ minWidth: 280 }}
                >
                  Hierarchy
                </th>
                {columns.map((c) => (
                  <th
                    key={c.id}
                    className={cn(
                      'px-3 font-medium',
                      cellPaddingY,
                      'border-b border-border',
                      c.align === 'left' ? 'text-left' : 'text-right',
                      c.className,
                    )}
                    style={c.width ? { width: c.width } : undefined}
                  >
                    {c.helpText ? (
                      <Tooltip content={c.helpText}>
                        <span className="cursor-help underline decoration-dotted underline-offset-2">
                          {c.header}
                        </span>
                      </Tooltip>
                    ) : (
                      c.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-6 text-center text-sm text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && isError && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-6 text-center text-sm text-destructive"
                  >
                    Failed to load.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && visible.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-10 text-center text-sm text-muted-foreground"
                  >
                    {emptyTitle}
                  </td>
                </tr>
              )}
              {!isLoading && !isError &&
                visible.map(({ row, depth, hasChildren, expanded: isOpen }) => {
                  const isSelected = selectedId === row.id;
                  const accessory = rowAccessory ? rowAccessory(row) : null;
                  const isParent =
                    row.level === 'category' || row.level === 'subcategory';
                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'group transition-colors',
                        'hover:bg-surface-elevated/40',
                        isSelected && 'bg-primary/10',
                        isParent && 'font-medium',
                      )}
                    >
                      <td
                        className={cn(
                          'sticky left-0 z-10 bg-surface px-4 align-middle',
                          cellPaddingY,
                          'border-b border-border/60',
                          'group-hover:bg-surface-elevated/40',
                          isSelected && 'bg-primary/10',
                        )}
                      >
                        <div
                          className="flex items-center gap-2"
                          style={{ paddingLeft: depth * 16 }}
                        >
                          <button
                            type="button"
                            className={cn(
                              'rounded p-0.5 text-muted-foreground transition-colors',
                              hasChildren
                                ? 'hover:bg-surface-elevated hover:text-foreground'
                                : 'invisible',
                            )}
                            onClick={() => hasChildren && toggle(row.id)}
                            aria-label={isOpen ? 'Collapse' : 'Expand'}
                            tabIndex={hasChildren ? 0 : -1}
                          >
                            {isOpen ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            className={cn(
                              'truncate text-left',
                              onRowSelect ? 'hover:text-primary' : 'cursor-default',
                            )}
                            onClick={() => onRowSelect?.(row)}
                            title={row.label}
                          >
                            {row.label}
                          </button>
                          {row.warnings?.includes('negative_margin') && (
                            <Tooltip content="Selling price is below cost on at least one SKU below this row.">
                              <span className="rounded bg-destructive/10 px-1.5 text-[10px] font-medium uppercase text-destructive">
                                neg
                              </span>
                            </Tooltip>
                          )}
                          {accessory && (
                            <span className="ml-2 shrink-0">{accessory}</span>
                          )}
                        </div>
                      </td>
                      {columns.map((c) => (
                        <td
                          key={c.id}
                          className={cn(
                            'px-3 align-middle',
                            cellPaddingY,
                            'border-b border-border/60',
                            c.align === 'left' ? 'text-left' : 'text-right',
                            'tabular-nums',
                            c.className,
                          )}
                          style={c.width ? { width: c.width } : undefined}
                        >
                          {c.render(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
